import '../contracts/Localidades_BuscabairrosResponse';
import '../contracts/Localidades_TogglerelacaoResponse';
import '../contracts/Localidades_UpdaterelacaoResponse';
import { ref, Ref } from 'vue'
import { httpService } from '../services/httpService'
import { log } from '../utils/DebugLogger'
import { validate } from '../contracts/validate'
import type { Localidades_ToggleRelacaoInput } from '../contracts/Localidades_ToggleRelacaoInput'
import type { Localidades_UpdateRelacaoInput } from '../contracts/Localidades_UpdateRelacaoInput'
import '../contracts/Localidades_ToggleRelacaoInput'
import '../contracts/Localidades_UpdateRelacaoInput'
import type {
  Localidades_BuscapaisesResponse,
  Localidades_BuscapaisesResponseDados,
} from '../contracts/Localidades_BuscapaisesResponse'
import '../contracts/Localidades_BuscapaisesResponse'
import type {
  Localidades_BuscaestadosResponse,
  Localidades_BuscaestadosResponseDados,
} from '../contracts/Localidades_BuscaestadosResponse'
import '../contracts/Localidades_BuscaestadosResponse'
import type {
  Localidades_BuscacidadesResponse,
  Localidades_BuscacidadesResponseDados,
} from '../contracts/Localidades_BuscacidadesResponse'
import '../contracts/Localidades_BuscacidadesResponse'

/**
 * Generic shape consumers can rely on regardless of which list endpoint a
 * value came from. The concrete shapes live in the response contracts above
 * (PascalCase ID / Name / etc., mirroring the Go locations-service structs).
 */
export interface LocalidadeEntity {
  id?: number
  nome?: string
  [key: string]: any
}

export function useLocalidades() {
  const isLoading: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  const fetchPaises = async (): Promise<Localidades_BuscapaisesResponseDados> => {
    log('useLocalidades', 'fetchPaises', 'Fetching countries')
    isLoading.value = true
    error.value = null

    try {
      const response = await httpService.get<Localidades_BuscapaisesResponse>(
        '/index.php/localidades/buscaPaises',
        { responseContract: 'Localidades_BuscapaisesResponse' }
      )
      log('useLocalidades', 'fetchPaises', 'Response', response)

      if (response.dados) {
        return response.dados
      } else {
        throw new Error('Erro ao buscar países')
      }
    } catch (err: any) {
      log('useLocalidades', 'fetchPaises', 'Error', err)
      error.value = err?.message || 'Erro ao buscar países'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchEstados = async (paisId: number | null = null): Promise<Localidades_BuscaestadosResponseDados> => {
    log('useLocalidades', 'fetchEstados', 'Fetching states', paisId)
    isLoading.value = true
    error.value = null

    try {
      const url = paisId ? `/index.php/localidades/buscaEstados/${paisId}` : '/index.php/localidades/buscaEstados'

      const response = await httpService.get<Localidades_BuscaestadosResponse>(
        url,
        { responseContract: 'Localidades_BuscaestadosResponse' }
      )
      log('useLocalidades', 'fetchEstados', 'Response', response)

      if (response.dados) {
        return response.dados
      } else {
        throw new Error('Erro ao buscar estados')
      }
    } catch (err: any) {
      log('useLocalidades', 'fetchEstados', 'Error', err)
      error.value = err?.message || 'Erro ao buscar estados'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchCidades = async (estadoId: number): Promise<Localidades_BuscacidadesResponseDados> => {
    log('useLocalidades', 'fetchCidades', 'Fetching cities', estadoId)
    isLoading.value = true
    error.value = null

    try {
      const response = await httpService.get<Localidades_BuscacidadesResponse>(
        `/index.php/localidades/buscaCidades/${estadoId}`,
        { responseContract: 'Localidades_BuscacidadesResponse' }
      )
      log('useLocalidades', 'fetchCidades', 'Response', response)

      if (response.dados) {
        return response.dados
      } else {
        throw new Error('Erro ao buscar cidades')
      }
    } catch (err: any) {
      log('useLocalidades', 'fetchCidades', 'Error', err)
      error.value = err?.message || 'Erro ao buscar cidades'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const fetchBairros = async (cidadeId: number): Promise<LocalidadeEntity[]> => {
    log('useLocalidades', 'fetchBairros', 'Fetching neighborhoods', cidadeId)
    isLoading.value = true
    error.value = null

    try {
      const response: any = await httpService.get(`/index.php/localidades/buscaBairros/${cidadeId}`, { responseContract: 'Localidades_BuscabairrosResponse' })
      log('useLocalidades', 'fetchBairros', 'Response', response)

      if (response.dados) {
        return response.dados
      } else {
        throw new Error('Erro ao buscar bairros')
      }
    } catch (err: any) {
      log('useLocalidades', 'fetchBairros', 'Error', err)
      error.value = err?.message || 'Erro ao buscar bairros'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const toggleRelacao = async (relacaoData: Localidades_ToggleRelacaoInput): Promise<any> => {
    log('useLocalidades', 'toggleRelacao', 'Toggling location relation', relacaoData)
    isLoading.value = true
    error.value = null

    try {
      validate('Localidades_ToggleRelacaoInput', relacaoData as any)
      const response: any = await httpService.post('/index.php/localidades/toggleRelacao', relacaoData, { responseContract: 'Localidades_TogglerelacaoResponse' })
      log('useLocalidades', 'toggleRelacao', 'Response', response)

      if (response.dados !== undefined) {
        return response.dados
      } else {
        throw new Error('Erro ao alternar relação de localidade')
      }
    } catch (err: any) {
      log('useLocalidades', 'toggleRelacao', 'Error', err)
      error.value = err?.message || 'Erro ao alternar relação'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const updateRelacao = async (relacaoData: Localidades_UpdateRelacaoInput): Promise<any> => {
    log('useLocalidades', 'updateRelacao', 'Updating location relation', relacaoData)
    isLoading.value = true
    error.value = null

    try {
      validate('Localidades_UpdateRelacaoInput', relacaoData as any)
      const response: any = await httpService.post('/index.php/localidades/updateRelacao', relacaoData, { responseContract: 'Localidades_UpdaterelacaoResponse' })
      log('useLocalidades', 'updateRelacao', 'Response', response)

      if (response.dados !== undefined) {
        return response.dados
      } else {
        throw new Error('Erro ao atualizar relação de localidade')
      }
    } catch (err: any) {
      log('useLocalidades', 'updateRelacao', 'Error', err)
      error.value = err?.message || 'Erro ao atualizar relação'
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    error,
    fetchPaises,
    fetchEstados,
    fetchCidades,
    fetchBairros,
    toggleRelacao,
    updateRelacao
  }
}
